/**
 * Add-device modal, shared.
 *
 * Lifted verbatim from the Location Hub (`pages/sites/detail.tsx`) so the
 * estimate/invoice line editor can add-and-link a device without forking the
 * jurisdiction-aware create flow. The server's asset-create gate (required
 * jurisdiction fields, USC warning, duplicate-serial conflict) is identical
 * wherever this renders — one modal, one source of truth.
 *
 * `onCreated` hands the caller the freshly created asset row so it can select
 * and link it immediately; `onClose` dismisses the overlay.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    BackflowDeviceFields,
    ASSEMBLY_LABEL,
    isDetectorAssembly,
    type BackflowDeviceFieldValues,
} from "@/components/backflow-device-fields";
import {
    useRequiredAssetFields,
    DynamicAssetFields,
    type DynamicAssetValues,
} from "@/components/assets/DynamicAssetFields";
import { DeviceConflictPanel, parseDeviceConflict, type DeviceConflict } from "@/components/duplicate-device-conflict";
import { X } from "lucide-react";
import { toast } from "sonner";

export const SERVICE_TYPES = [
    { value: "fireline", label: "Fireline" },
    { value: "fireline_bypass", label: "Fireline Bypass" },
    { value: "irrigation", label: "Irrigation" },
    { value: "domestic_mainline", label: "Domestic Mainline" },
    { value: "domestic_other", label: "Domestic-Other" },
    { value: "other", label: "Other" },
];

export const SERVICE_LABEL: Record<string, string> = Object.fromEntries(SERVICE_TYPES.map((t) => [t.value, t.label]));

/** Drop empty/blank dynamic asset values so we only send filled jurisdiction fields. */
export function cleanDynamicValues(values: DynamicAssetValues): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
        if (typeof v === "string" && v.trim() !== "") out[k] = v.trim();
    }
    return out;
}

/** Surface the server's real reason (jurisdiction fields, USC gate, etc.) instead of a generic toast. */
export function deviceErrorMessage(e: unknown, fallback: string): string {
    if (e instanceof ApiError) {
        const body = e.body as { message?: string; error?: string } | undefined;
        if (body?.message) return body.message;
    }
    return fallback;
}

/** The subset of the created asset row the link callers need. */
export interface CreatedAsset {
    id: string;
    name: string;
    size_label?: string | null;
    serial_number?: string | null;
    [key: string]: unknown;
}

export function AddDeviceToSiteModal({
    siteId,
    onClose,
    onCreated,
}: {
    siteId: string;
    onClose: () => void;
    onCreated?: (asset: CreatedAsset) => void;
}) {
    const qc = useQueryClient();

    const [name, setName] = useState("");
    const [device, setDevice] = useState<BackflowDeviceFieldValues>({
        assemblyType: "",
        sizeLabel: "",
        manufacturer: "",
        model: "",
        serialNumber: "",
        pipeMaterial: "",
    });
    const { assemblyType, sizeLabel, manufacturer, model, serialNumber } = device;
    const patchDevice = (patch: Partial<BackflowDeviceFieldValues>) => setDevice((d) => ({ ...d, ...patch }));
    const [serviceType, setServiceType] = useState("");
    const [serviceTypeOther, setServiceTypeOther] = useState("");
    const [meterNumber, setMeterNumber] = useState("");
    const [locationDescription, setLocationDescription] = useState("");

    const needsMeter = isDetectorAssembly(assemblyType);

    // Jurisdiction-required asset fields (hazard, occupancy, device type,
    // etc.) the active form template demands at create time — the same set
    // the field tech captures. Without these the server's create gate
    // rejects with ASSET_FIELDS_MISSING. "Serves" is excluded because the
    // modal already has its own Serves <select>.
    const required = useRequiredAssetFields(siteId, "backflow");
    const dynamicColumns = (required.data?.requiredColumns ?? []).filter((c) => c.path !== "asset.serves");
    const [dynamicValues, setDynamicValues] = useState<DynamicAssetValues>({});
    const [conflict, setConflict] = useState<DeviceConflict | null>(null);

    const create = useMutation({
        mutationFn: ({ allowDuplicate = false }: { allowDuplicate?: boolean } = {}) => {
            let finalName = name.trim();
            if (!finalName) {
                const typePart = assemblyType ? (ASSEMBLY_LABEL[assemblyType] ?? assemblyType.toUpperCase()) : "";
                const sizePart = sizeLabel || "";
                const servesPart = serviceType && serviceType !== "other" ? (SERVICE_LABEL[serviceType] ?? "") : "";
                finalName = [typePart, sizePart, servesPart].filter(Boolean).join(" ") || "Asset";
            }
            return api.post<{ asset: CreatedAsset }>(`/api/sites/${siteId}/assets`, {
                name: finalName,
                assemblyType: assemblyType || undefined,
                sizeLabel: sizeLabel || undefined,
                serialNumber: serialNumber.trim() || undefined,
                manufacturer: manufacturer.trim() || undefined,
                model: model.trim() || undefined,
                serviceType: serviceType || undefined,
                serviceTypeOther:
                    serviceType === "domestic_other" || serviceType === "other"
                        ? serviceTypeOther.trim() || undefined
                        : undefined,
                meterNumber: needsMeter ? meterNumber.trim() || undefined : undefined,
                locationDescription: locationDescription.trim() || undefined,
                // Provenance rides with the material; the server defaults the
                // source to "human" if the tech typed it rather than scanned.
                pipeMaterial: device.pipeMaterial || undefined,
                pipeMaterialSource: device.pipeMaterial ? (device.pipeMaterialSource ?? "human") : undefined,
                pipeMaterialConfidence: device.pipeMaterial ? device.pipeMaterialConfidence : undefined,
                allowDuplicate: allowDuplicate || undefined,
                ...cleanDynamicValues(dynamicValues),
            });
        },
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ["site-summary", siteId] });
            qc.invalidateQueries({ queryKey: ["site-trade-assets", siteId] });
            toast.success("Device added");
            if (data?.asset) onCreated?.(data.asset);
            onClose();
        },
        onError: (e) => {
            const dup = parseDeviceConflict(e);
            if (dup) {
                setConflict(dup);
                return;
            }
            toast.error(deviceErrorMessage(e, "Couldn't create device"));
        },
    });

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="w-full max-w-xl rounded-lg border bg-card shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">Add device to location</h3>
                    <button type="button" onClick={onClose} className="text-foreground/80 hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <form
                    className="p-4 space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setConflict(null);
                        create.mutate({});
                    }}
                >
                    <BackflowDeviceFields value={device} onChange={patchDevice} siteId={siteId} />

                    <div>
                        <label className="text-xs font-medium">Serves</label>
                        <select
                            value={serviceType}
                            onChange={(e) => setServiceType(e.target.value)}
                            className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                        >
                            <option value="">— Select —</option>
                            {SERVICE_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {(serviceType === "domestic_other" || serviceType === "other") && (
                        <div>
                            <label className="text-xs font-medium">Describe what it serves</label>
                            <Input value={serviceTypeOther} onChange={(e) => setServiceTypeOther(e.target.value)} />
                        </div>
                    )}

                    <DynamicAssetFields
                        requiredColumns={dynamicColumns}
                        values={dynamicValues}
                        onChange={setDynamicValues}
                        className="space-y-4"
                        fieldClassName=""
                    />

                    {needsMeter && (
                        <div>
                            <label className="text-xs font-medium">Meter #</label>
                            <Input value={meterNumber} onChange={(e) => setMeterNumber(e.target.value)} />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Location on property</label>
                            <Input
                                value={locationDescription}
                                onChange={(e) => setLocationDescription(e.target.value)}
                                placeholder='e.g. "At the meter, right side of driveway"'
                            />
                            {/* #819 B10 M10: the one field that should hold
                                "where is it" names its reader — like Job Notes. */}
                            <p className="text-xs text-foreground/80 mt-1">
                                A technician reads this to find the device in a parking lot. This field is the one
                                answer to &ldquo;where is it&rdquo; — don&apos;t repeat it in the name or the job
                                summary.
                            </p>
                        </div>
                        <div>
                            <label className="text-xs font-medium">
                                Name / label <span className="text-foreground/80 font-normal">(optional)</span>
                            </label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Auto-generated if blank"
                            />
                        </div>
                    </div>

                    {conflict && (
                        <DeviceConflictPanel
                            conflict={conflict}
                            onUseExisting={() => onClose()}
                            onCreateAnyway={
                                conflict.kind === "possible" ? () => create.mutate({ allowDuplicate: true }) : undefined
                            }
                            createAnywayPending={create.isPending}
                        />
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" size="sm" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" disabled={create.isPending}>
                            {create.isPending ? "Adding…" : "Add device"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
